import { fetchJson } from "../lib/api";
import type { PriceVarianceRow } from "./reports";

export type PriceReportSelection = {
  period?: "last_month" | "custom";
  from: string;
  to: string;
  supplier: string;
  project: string;
  costCenter?: string;
  costCenterType?: string;
  returnTo?: string;
  threshold: number;
  difference: number;
};
export type PriceReportGroup = {
  article_group_key: string | null;
  supplier: string | null;
  article_code: string | null;
  description: string | null;
  line_count: number;
  document_count: number;
  difference: string;
  reviewed_count: number;
  open_count: number;
};
export type PriceReport = {
  freshness?: { visit_id: string | null; since: number | null; until: number; new_count: number } | null;
  review_counts: ReviewCounts;
  first_open: OpenPosition | null;
  coverage: {
    total: number;
    sufficient: number;
    insufficient: number;
    missing: number;
    unavailable: number;
    refreshed_at: string | null;
  };
  finding_count: number;
  difference: string;
  total_groups: number;
  items: PriceReportGroup[];
  positions: PriceReportPosition[];
};

export type PriceReportPosition = Pick<PriceVarianceRow,
  "invoice_doc_id" | "doc_number" | "doc_date" | "line_pos" | "article_group_key" |
  "description" | "primary_article_code" | "supplier" | "qty" | "uom" |
  "net_unit_price" | "canonical_baseline_price" | "canonical_delta_pct" |
  "canonical_overpay_abs" | "canonical_baseline_line_count" |
  "canonical_baseline_document_count" | "price_anomaly_status"
> & { is_price_anomaly: boolean; review_token: string; task_reason?: string | null; task_observed_at?: string | null };

export function priceReportParams(selection: PriceReportSelection): URLSearchParams {
  const params = new URLSearchParams({
    min_delta_pct: String(selection.threshold), min_difference: String(selection.difference),
  });
  if (selection.from) params.set("from", selection.from);
  if (selection.to) params.set("to", selection.to);
  if (selection.costCenter) params.set("cost_center", selection.costCenter);
  if (selection.costCenterType) params.set("cost_center_type", selection.costCenterType);
  if (selection.returnTo) params.set("return_to", selection.returnTo);
  if (selection.supplier) params.set("supplier", selection.supplier);
  if (selection.project) params.set("project", selection.project);
  return params;
}

export function fetchPriceReport(selection: PriceReportSelection, offset: number, article?: string, positions = false, visitId?: string, newOnly = false, createVisit = false): Promise<PriceReport> {
  const params = priceReportParams(selection);
  params.set("offset", String(offset));
  params.set("limit", "50");
  if (article) params.set("article", article);
  if (positions) params.set("positions", "true");
  if (visitId) params.set("visit_id", visitId);
  if (newOnly) params.set("new_only", "true");
  return fetchJson<PriceReport>(`/api/reports/price-report${createVisit ? "/visit" : ""}?${params}`, createVisit ? { method: "POST" } : undefined);
}

export function fetchPriceReportOptions(): Promise<{ suppliers: string[] }> {
  return fetchJson<{ suppliers: string[] }>("/api/reports/price-report/options");
}

export type ReviewCounts = { open: number; in_review: number; resolved: number; report_only: number };
export type OpenPosition = { invoice: string; position: string; offset: number };
export type PriceReportEvidence = {
  reference_price: string | null; reference_kind: string | null;
  first_date: string | null; last_date: string | null;
  history_matches: boolean; total: number;
  items: { invoice_doc_id: string; doc_number: string | null; line_pos: string; doc_date: string; supplier: string | null; page_no: number | null; price: string; qty: string | null; uom: string | null }[];
  decision: { action: string; status: string; comment: string | null; created_at: string; previous_price: string | null; reference_change: "changed" | "unchanged" | "unavailable" } | null;
};
export function fetchPriceReportEvidence(row: PriceReportPosition, offset: number): Promise<PriceReportEvidence> {
  const params = new URLSearchParams({ invoice: row.invoice_doc_id ?? "", position: String(row.line_pos), token: row.review_token, offset: String(offset) });
  return fetchJson<PriceReportEvidence>(`/api/reports/price-report/evidence?${params}`);
}
